/**
 * Enterprise Cloud Security Module Component
 * Cloud Security Posture Management (CSPM)
 */

import React, { useState, useEffect } from 'react';
import { useI18n } from '../../context/I18nContext';
import { cloudService } from '../../services/enterpriseService';
import { grcService } from '../../services/grcService';
import { Modal, ControlSelector, useEvidenceIntegration } from './SharedModals';
import {
  Cloud, Server, Shield, AlertTriangle, CheckCircle, XCircle,
  RefreshCw, Plus, Settings, Search, Filter, ChevronRight,
  AlertOctagon, TrendingUp, Eye, Zap, Lock, Unlock, LinkIcon
} from 'lucide-react';

interface CloudIntegration {
  id: number;
  name: string;
  provider: 'aws' | 'azure' | 'gcp' | 'other';
  integration_type: string;
  connection_status: 'connected' | 'disconnected' | 'error';
  last_sync_at?: string;
  config: Record<string, any>;
  created_at: string;
}

interface CloudFinding {
  id: number;
  integration_id: number;
  resource_type: string;
  resource_id: string;
  finding_type: string;
  severity: 'critical' | 'high' | 'medium' | 'low' | 'info';
  title: string;
  description?: string;
  remediation?: string;
  status: 'open' | 'resolved' | 'suppressed' | 'false_positive';
  detected_at: string;
}

interface CloudStats {
  total_integrations: number;
  connected: number;
  total_findings: number;
  critical: number;
  high: number;
  medium: number;
  low: number;
}

export const EnterpriseCloud: React.FC<{ frameworkName?: string }> = ({ frameworkName }) => {
  const { language } = useI18n();
  const [integrations, setIntegrations] = useState<CloudIntegration[]>([]);
  const [findings, setFindings] = useState<CloudFinding[]>([]);
  const [stats, setStats] = useState<CloudStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'overview' | 'integrations' | 'findings'>('overview');
  const [searchQuery, setSearchQuery] = useState('');

  // Modal states
  const [showNewIntegrationModal, setShowNewIntegrationModal] = useState(false);
  const [showLinkControlsModal, setShowLinkControlsModal] = useState(false);
  const [selectedFinding, setSelectedFinding] = useState<CloudFinding | null>(null);
  const [saving, setSaving] = useState(false);

  // New integration form
  const [newIntegration, setNewIntegration] = useState({
    name: '',
    provider: 'aws' as CloudIntegration['provider'],
    integration_type: 'cspm'
  });

  // Control linking
  const [frameworks, setFrameworks] = useState<any[]>([]);
  const [selectedFramework, setSelectedFramework] = useState<number | null>(null);
  const [selectedControls, setSelectedControls] = useState<number[]>([]);
  const { linkCloudFindingToControl } = useEvidenceIntegration();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [integrationsData, findingsData, statsData] = await Promise.all([
        cloudService.getIntegrations(),
        cloudService.getFindings().catch(() => ({ findings: [] })),
        cloudService.getFindingsSummary().catch(() => null)
      ]);
      
      // Handle API response format - may be { data: [...] }, { integrations: [...] }, or direct array
      const integrationsList = integrationsData?.data || integrationsData?.integrations || (Array.isArray(integrationsData) ? integrationsData : []);
      const findingsList = findingsData?.data || findingsData?.findings || (Array.isArray(findingsData) ? findingsData : []);
      setIntegrations(integrationsList);
      setFindings(findingsList);
      setStats(statsData);
    } catch (error) {
      console.error('Error loading cloud data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async (integrationId: number) => {
    try {
      await cloudService.syncIntegration(integrationId);
      loadData();
    } catch (error) {
      console.error('Error syncing integration:', error);
    }
  };

  const loadFrameworks = async () => {
    try {
      const data = await grcService.getFrameworks();
      const frameworkList = Array.isArray(data) ? data : ((data as any)?.frameworks || []);
      setFrameworks(frameworkList);
    } catch (error) {
      console.error('Error loading frameworks:', error);
    }
  };

  const handleAddIntegration = async () => {
    if (!newIntegration.name) return;
    setSaving(true);
    try {
      await cloudService.createIntegration(newIntegration);
      setShowNewIntegrationModal(false);
      setNewIntegration({ name: '', provider: 'aws', integration_type: 'cspm' });
      loadData();
    } catch (error) {
      console.error('Error adding integration:', error);
    } finally {
      setSaving(false);
    }
  };

  const openLinkModal = (finding: CloudFinding) => {
    setSelectedFinding(finding);
    setSelectedControls([]);
    setSelectedFramework(null);
    loadFrameworks();
    setShowLinkControlsModal(true);
  };

  const handleLinkToControls = async () => {
    if (!selectedFinding || selectedControls.length === 0) return;
    setSaving(true);
    try {
      for (const controlId of selectedControls) {
        await linkCloudFindingToControl(
          controlId,
          selectedFinding.id,
          selectedFinding.title,
          selectedFinding.severity
        );
      }
      setShowLinkControlsModal(false);
      setSelectedFinding(null);
    } catch (error) {
      console.error('Error linking to controls:', error);
    } finally {
      setSaving(false);
    }
  };

  const providers = {
    aws: { label: 'Amazon AWS', color: 'orange', icon: '☁️' },
    azure: { label: 'Microsoft Azure', color: 'blue', icon: '☁️' },
    gcp: { label: 'Google Cloud', color: 'green', icon: '☁️' },
    other: { label: 'Other', color: 'gray', icon: '☁️' }
  };

  const severityColors = {
    critical: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
    high: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300',
    medium: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300',
    low: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
    info: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
  };

  const statusColors = {
    connected: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
    disconnected: 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400',
    error: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
  };

  const openFindings = findings.filter(f => f.status === 'open');

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-indigo-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
            <Cloud className="w-7 h-7 text-blue-500" />
            {language === 'sr' ? 'Cloud sigurnost' : 'Cloud Security Posture'}
            {frameworkName && (
              <span className="px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full text-sm font-medium">
                {frameworkName}
              </span>
            )}
          </h2>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            {language === 'sr' 
              ? 'Pratite sigurnosno stanje vaših cloud resursa'
              : 'Monitor security posture of your cloud resources'
            }
          </p>
        </div>
        <button 
          onClick={() => setShowNewIntegrationModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="w-4 h-4" />
          {language === 'sr' ? 'Nova integracija' : 'New Integration'}
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
          <div className="flex items-center justify-between mb-3">
            <Server className="w-8 h-8 text-blue-500" />
          </div>
          <div className="text-3xl font-bold text-gray-900 dark:text-white">{integrations.length}</div>
          <div className="text-sm text-gray-500 dark:text-gray-400">{language === 'sr' ? 'Integracija' : 'Integrations'}</div>
        </div>
        
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
          <div className="flex items-center justify-between mb-3">
            <AlertOctagon className="w-8 h-8 text-red-500" />
          </div>
          <div className="text-3xl font-bold text-gray-900 dark:text-white">
            {openFindings.filter(f => f.severity === 'critical').length}
          </div>
          <div className="text-sm text-gray-500 dark:text-gray-400">{language === 'sr' ? 'Kritično' : 'Critical'}</div>
        </div>
        
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
          <div className="flex items-center justify-between mb-3">
            <AlertTriangle className="w-8 h-8 text-orange-500" />
          </div>
          <div className="text-3xl font-bold text-gray-900 dark:text-white">
            {openFindings.filter(f => f.severity === 'high').length}
          </div>
          <div className="text-sm text-gray-500 dark:text-gray-400">{language === 'sr' ? 'Visoko' : 'High'}</div>
        </div>
        
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
          <div className="flex items-center justify-between mb-3">
            <Shield className="w-8 h-8 text-yellow-500" />
          </div>
          <div className="text-3xl font-bold text-gray-900 dark:text-white">
            {openFindings.filter(f => f.severity === 'medium').length}
          </div>
          <div className="text-sm text-gray-500 dark:text-gray-400">{language === 'sr' ? 'Srednje' : 'Medium'}</div>
        </div>
        
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
          <div className="flex items-center justify-between mb-3">
            <CheckCircle className="w-8 h-8 text-emerald-500" />
          </div>
          <div className="text-3xl font-bold text-gray-900 dark:text-white">
            {findings.filter(f => f.status === 'resolved').length}
          </div>
          <div className="text-sm text-gray-500 dark:text-gray-400">{language === 'sr' ? 'Riješeno' : 'Resolved'}</div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-gray-200 dark:border-gray-700">
        {[
          { key: 'overview', icon: TrendingUp, label: language === 'sr' ? 'Pregled' : 'Overview' },
          { key: 'integrations', icon: Server, label: language === 'sr' ? 'Integracije' : 'Integrations' },
          { key: 'findings', icon: AlertTriangle, label: language === 'sr' ? 'Nalazi' : 'Findings' }
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key as typeof activeTab)}
            className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors ${
              activeTab === tab.key
                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
            {tab.key === 'findings' && openFindings.length > 0 && (
              <span className="ml-1 px-2 py-0.5 bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300 rounded-full text-xs">
                {openFindings.length}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Connected Providers */}
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <Cloud className="w-5 h-5 text-blue-500" />
              {language === 'sr' ? 'Cloud provideri' : 'Cloud Providers'}
            </h3>
            <div className="space-y-3">
              {integrations.map(integration => (
                <div key={integration.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center text-xl">
                      {providers[integration.provider]?.icon || '☁️'}
                    </div>
                    <div>
                      <div className="font-medium text-gray-900 dark:text-white">{integration.name}</div>
                      <div className="text-xs text-gray-500">{providers[integration.provider]?.label}</div>
                    </div>
                  </div>
                  <span className={`px-2 py-1 rounded text-xs ${statusColors[integration.connection_status]}`}>
                    {integration.connection_status}
                  </span>
                </div>
              ))}
              {integrations.length === 0 && (
                <p className="text-center py-4 text-gray-500 dark:text-gray-400">
                  {language === 'sr' ? 'Nema povezanih providera' : 'No providers connected'}
                </p>
              )}
            </div>
          </div>

          {/* Critical Findings */}
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <AlertOctagon className="w-5 h-5 text-red-500" />
              {language === 'sr' ? 'Kritični nalazi' : 'Critical Findings'}
            </h3>
            <div className="space-y-3">
              {openFindings.filter(f => ['critical', 'high'].includes(f.severity)).slice(0, 5).map(finding => (
                <div key={finding.id} className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-0.5 rounded text-xs ${severityColors[finding.severity]}`}>
                          {finding.severity.toUpperCase()}
                        </span>
                      </div>
                      <div className="font-medium text-gray-900 dark:text-white mt-1">{finding.title}</div>
                      <div className="text-xs text-gray-500 mt-1">{finding.resource_type}: {finding.resource_id}</div>
                    </div>
                    <button className="p-1 text-gray-400 hover:text-gray-600">
                      <Eye className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
              {openFindings.filter(f => ['critical', 'high'].includes(f.severity)).length === 0 && (
                <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                  <CheckCircle className="w-8 h-8 mx-auto mb-2 text-green-500" />
                  <p>{language === 'sr' ? 'Nema kritičnih nalaza' : 'No critical findings'}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Integrations Tab */}
      {activeTab === 'integrations' && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {integrations.map(integration => (
              <div key={integration.id} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center text-2xl">
                      {providers[integration.provider]?.icon || '☁️'}
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 dark:text-white">{integration.name}</h4>
                      <span className="text-xs text-gray-500">{providers[integration.provider]?.label}</span>
                    </div>
                  </div>
                  <span className={`px-2 py-1 rounded text-xs ${statusColors[integration.connection_status]}`}>
                    {integration.connection_status}
                  </span>
                </div>
                
                <div className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                  {integration.last_sync_at && (
                    <span>{language === 'sr' ? 'Zadnja sinkronizacija' : 'Last sync'}: {new Date(integration.last_sync_at).toLocaleString()}</span>
                  )}
                </div>
                
                <div className="flex items-center gap-2 pt-3 border-t border-gray-200 dark:border-gray-700">
                  <button 
                    onClick={() => handleSync(integration.id)}
                    className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 rounded-lg hover:bg-blue-200 dark:hover:bg-blue-900/50"
                  >
                    <RefreshCw className="w-4 h-4" />
                    {language === 'sr' ? 'Sinkroniziraj' : 'Sync'}
                  </button>
                  <button className="p-2 text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
                    <Settings className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Findings Tab */}
      {activeTab === 'findings' && (
        <div className="space-y-4">
          <div className="flex items-center gap-4">
            <div className="flex-1 relative">
              <Search className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={language === 'sr' ? 'Pretraži nalaze...' : 'Search findings...'}
                className="w-full pl-10 pr-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
              />
            </div>
            <button className="flex items-center gap-2 px-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">
              <Filter className="w-4 h-4" />
              {language === 'sr' ? 'Filter' : 'Filter'}
            </button>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-700/50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{language === 'sr' ? 'Ozbiljnost' : 'Severity'}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{language === 'sr' ? 'Naziv' : 'Title'}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{language === 'sr' ? 'Resurs' : 'Resource'}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{language === 'sr' ? 'Status' : 'Status'}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{language === 'sr' ? 'Otkriveno' : 'Detected'}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {findings.map(finding => (
                  <tr key={finding.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded text-xs ${severityColors[finding.severity]}`}>
                        {finding.severity}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{finding.title}</td>
                    <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">{finding.resource_type}</td>
                    <td className="px-4 py-3 text-sm">{finding.status}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">{new Date(finding.detected_at).toLocaleDateString()}</td>
                    <td className="px-4 py-3 text-sm">
                      <button 
                        onClick={() => openLinkModal(finding)}
                        className="p-1 text-indigo-400 hover:text-indigo-600" 
                        title={language === 'sr' ? 'Poveži sa kontrolama' : 'Link to controls'}
                      >
                        <LinkIcon className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {findings.length === 0 && (
              <p className="text-center py-8 text-gray-500 dark:text-gray-400">
                {language === 'sr' ? 'Nema nalaza' : 'No findings'}
              </p>
            )}
          </div>
        </div>
      )}

      {/* New Integration Modal */}
      <Modal
        isOpen={showNewIntegrationModal}
        onClose={() => setShowNewIntegrationModal(false)}
        title={language === 'sr' ? 'Nova cloud integracija' : 'New Cloud Integration'}
        footer={
          <div className="flex justify-end gap-3">
            <button
              onClick={() => setShowNewIntegrationModal(false)}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              {language === 'sr' ? 'Otkaži' : 'Cancel'}
            </button>
            <button
              onClick={handleAddIntegration}
              disabled={saving || !newIntegration.name}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? (language === 'sr' ? 'Dodavanje...' : 'Adding...') : (language === 'sr' ? 'Dodaj' : 'Add')}
            </button>
          </div>
        }
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {language === 'sr' ? 'Naziv integracije *' : 'Integration Name *'}
            </label>
            <input
              type="text"
              value={newIntegration.name}
              onChange={(e) => setNewIntegration({...newIntegration, name: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              placeholder="Production AWS Account"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {language === 'sr' ? 'Cloud provajder' : 'Cloud Provider'}
            </label>
            <select
              value={newIntegration.provider}
              onChange={(e) => setNewIntegration({...newIntegration, provider: e.target.value as CloudIntegration['provider']})}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="aws">Amazon AWS</option>
              <option value="azure">Microsoft Azure</option>
              <option value="gcp">Google Cloud Platform</option>
              <option value="other">{language === 'sr' ? 'Ostalo' : 'Other'}</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {language === 'sr' ? 'Tip integracije' : 'Integration Type'}
            </label>
            <select
              value={newIntegration.integration_type}
              onChange={(e) => setNewIntegration({...newIntegration, integration_type: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="cspm">CSPM (Cloud Security Posture)</option>
              <option value="cwpp">CWPP (Cloud Workload Protection)</option>
              <option value="ciem">CIEM (Cloud Infrastructure Entitlements)</option>
            </select>
          </div>
          <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
            <p className="text-sm text-blue-700 dark:text-blue-300">
              {language === 'sr' 
                ? 'Nakon dodavanja, konfigurirajte pristupne podatke u postavkama integracije.'
                : 'After adding, configure access credentials in integration settings.'
              }
            </p>
          </div>
        </div>
      </Modal>

      {/* Link to Controls Modal */}
      <Modal
        isOpen={showLinkControlsModal}
        onClose={() => setShowLinkControlsModal(false)}
        title={language === 'sr' ? 'Poveži nalaz sa kontrolama' : 'Link Finding to Controls'}
        footer={
          <div className="flex justify-end gap-3">
            <button
              onClick={() => setShowLinkControlsModal(false)}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              {language === 'sr' ? 'Otkaži' : 'Cancel'}
            </button>
            <button
              onClick={handleLinkToControls}
              disabled={saving || selectedControls.length === 0}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
            >
              {saving ? (language === 'sr' ? 'Čuvanje...' : 'Saving...') : (language === 'sr' ? 'Poveži' : 'Link')}
            </button>
          </div>
        }
      >
        {selectedFinding && (
          <div className="space-y-4">
            <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <div className="flex items-center gap-2 mb-1">
                <span className={`px-2 py-0.5 rounded text-xs ${severityColors[selectedFinding.severity]}`}>
                  {selectedFinding.severity.toUpperCase()}
                </span>
              </div>
              <div className="font-medium text-gray-900 dark:text-white">{selectedFinding.title}</div>
              <div className="text-sm text-gray-500 mt-1">{selectedFinding.resource_type}: {selectedFinding.resource_id}</div>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {language === 'sr' 
                ? 'Ovaj cloud nalaz će biti dodat kao dokaz za odabrane kontrole'
                : 'This cloud finding will be added as evidence to selected controls'
              }
            </p>
            <ControlSelector
              frameworks={frameworks}
              selectedFramework={selectedFramework}
              onSelectFramework={setSelectedFramework}
              selectedControls={selectedControls}
              onSelectControls={setSelectedControls}
            />
          </div>
        )}
      </Modal>
    </div>
  );
};

export default EnterpriseCloud;
